import React, { useState, useRef, useLayoutEffect } from 'react';
import { Typography, Box } from '@mui/material';

interface TruncatedTextProps {
  text: string | null | undefined;
  maxLength?: number;
  sx?: object;
  variant?: 'body1' | 'body2' | 'h6' | 'subtitle1' | 'subtitle2' | 'caption' | 'overline';
  disabled?: boolean;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({
  text,
  sx = {},
  variant = 'body2',
  disabled = false
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!isExpanded && textRef.current) {
      setIsOverflowing(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, [text, isExpanded]);

  if (!text) {
    return <Typography sx={sx} variant={variant}>-</Typography>;
  }

  if (isExpanded) {
    return (
      <Box sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap', ...sx }}>
        <Typography variant={variant} component="span">
          {text}
        </Typography>
        <Box
          component="span"
          sx={{ cursor: disabled ? 'not-allowed' : 'pointer', marginLeft: '4px', display: 'inline-flex', verticalAlign: 'baseline' }}
          onClick={(e) => {
            e.stopPropagation();
            !disabled && setIsExpanded(false);
          }}          
        >
          <Typography variant="caption" component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>...less</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', overflow: 'hidden', ...sx }}>
      <Typography
        ref={textRef}
        variant={variant}
        component="span"
        sx={{ overflow: 'hidden', whiteSpace: 'nowrap', flexShrink: 1, minWidth: 0 }}
      >
        {text}
      </Typography>
      {isOverflowing && (
        <Box
          component="span"
          sx={{ cursor: disabled ? 'not-allowed' : 'pointer', marginLeft: '4px', display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            !disabled && setIsExpanded(true);
          }}
        >
          <Typography variant="caption" component="span" sx={{ fontSize: '0.65rem', color: 'text.secondary', lineHeight: 1 }}>...more</Typography>
        </Box>
      )}
    </Box>
  );
};

export default TruncatedText;